import React, { useState } from 'react'
import { DataTableViewer } from '../components/ui/DataTableViewer'

const sampleData = [
  { id: 1, name: 'John Doe', email: 'john@example.com', age: 30, city: 'New York', salary: 75000, commission_rate: 0.15, hire_date: '2020-03-15' },
  { id: 2, name: 'Jane Smith', email: 'jane@example.com', age: 25, city: 'Los Angeles', salary: 65000, commission_rate: 0.12, hire_date: '2021-07-22' },
  { id: 3, name: 'Bob Johnson', email: 'bob@example.com', age: 35, city: 'Chicago', salary: 85000, commission_rate: 0.18, hire_date: '2019-11-08' },
  { id: 4, name: 'Alice Brown', email: 'alice@example.com', age: 28, city: 'Houston', salary: 72000, commission_rate: 0.14, hire_date: '2022-01-10' },
  { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', age: 32, city: 'Phoenix', salary: 78000, commission_rate: 0.16, hire_date: '2020-09-30' },
]

export default function DataTableDemo() {
  const [data, setData] = useState(sampleData)

  const handleDataChange = (newData: Record<string, any>[]) => {
    setData(newData)
    console.log('Data changed:', newData)
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Data Table Demo</h1>
          <p className="text-gray-600">
            Interactive data table with TanStack Table featuring smart formatting, filtering, sorting, pagination, and single-click cell editing.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <DataTableViewer
            initialData={data}
            onDataChange={handleDataChange}
            enableSmartFormatting={true}
            currency="USD"
            locale="en-US"
          />
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold mb-4">Features</h2>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• <strong>CSV Upload:</strong> Upload CSV files to populate the table</li>
            <li>• <strong>Paste Data:</strong> Paste comma, tab, or semicolon-separated data</li>
            <li>• <strong>Global Search:</strong> Search across all columns</li>
            <li>• <strong>Toggleable Column Filters:</strong> Click "Filters" button to show/hide individual column filters</li>
            <li>• <strong>Sorting:</strong> Click column headers to sort</li>
            <li>• <strong>Pagination:</strong> Navigate through large datasets</li>
            <li>• <strong>Single-Click Editing:</strong> Click cells to edit values (hover to see edit hint)</li>
            <li>• <strong>Smart Formatting:</strong> Automatic detection and formatting of currency, percentages, dates, and numbers</li>
            <li>• <strong>Locale Support:</strong> Currency and number formatting based on locale</li>
            <li>• <strong>Add/Delete Rows:</strong> Add new rows or delete existing ones</li>
            <li>• <strong>Export CSV:</strong> Export data back to CSV format</li>
            <li>• <strong>Keyboard Navigation:</strong> Use arrow keys to navigate, Enter to save, Escape to cancel</li>
          </ul>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold mb-4">Interaction Model</h2>
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Single-Click Editing</h3>
              <p>
                Cells can be edited with a single click for improved user experience. When you hover over editable cells, 
                you'll see a blue highlight, edit icon, and "Click to edit" hint. This makes editing faster and more intuitive.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Toggleable Filters</h3>
              <p>
                To reduce UI clutter, column filters are now toggleable. Use the "Filters" button next to the search bar 
                to show/hide individual column filter inputs. This keeps the interface clean while providing powerful filtering capabilities.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Visual Feedback</h3>
              <p>
                Enhanced visual feedback includes blue hover highlights, clear edit indicators, and column type badges 
                showing detected data types (currency, percentage, date, etc.) in the header.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold mb-4">Sample CSV Data</h2>
          <p className="text-sm text-gray-600 mb-4">Copy and paste this sample data to test:</p>
          <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`name,email,age,city,department,salary,commission_rate,hire_date,total_sales
John Doe,john@example.com,30,New York,Engineering,75000,0.15,2020-03-15,125000
Jane Smith,jane@example.com,25,Los Angeles,Marketing,65000,0.12,2021-07-22,98000
Bob Johnson,bob@example.com,35,Chicago,Sales,70000,0.18,2019-11-08,145000
Alice Brown,alice@example.com,28,Houston,Engineering,72000,0.14,2022-01-10,110000
Charlie Wilson,charlie@example.com,32,Phoenix,HR,68000,0.16,2020-09-30,87000
Diana Davis,diana@example.com,29,Dallas,Marketing,66000,0.13,2021-02-14,92000
Eve Miller,eve@example.com,31,San Antonio,Engineering,74000,0.17,2020-08-05,132000
Frank Garcia,frank@example.com,27,San Diego,Sales,69000,0.19,2023-04-12,156000
Grace Martinez,grace@example.com,33,Austin,HR,71000,0.11,2019-12-20,78000
Henry Lopez,henry@example.com,26,Jacksonville,Marketing,64000,0.14,2022-06-08,103000`}
          </pre>
        </div>
      </div>
    </div>
  )
}